import { useCallback, useMemo, useState } from 'react';
import { apiClient } from '@/api/client';
import { useNotification } from '@/contexts/NotificationContext';
import { getStartHour, getDurationPalette, getBreakOptions } from '@/config/modeConfig';
import { officersWithUnassignedTime, type BreakOverrides } from '@/utils/unassignedOfficers';
import { useOfficerOrder } from '@/utils/officerOrder';
import { groupOfficersByType, OFFICER_TYPE_PILL, type OfficerTypeGroup } from '@/utils/officerGrouping';
import { MaxCounterDurationField } from '@/components/MaxCounterDurationField';
import { HandoffOverlapToggle } from '@/components/HandoffOverlapToggle';
import { ClearAssignmentsPicker, type AssignmentWindow } from '@/components/ClearAssignmentsPicker';
import type { OfficerMetadata, OperationResult } from '@/types/api';
import { UI_SCALE, popupMaxH } from '@/utils/uiScale';

// Break size options in 15-min slots — shared source, mirrors the Add SOS/OT UI.
const BREAK_OPTIONS = getBreakOptions();

interface Props {
  officer_schedules: Record<string, number[]>;
  officer_metadata: Record<string, OfficerMetadata>;
  shiftType: string;
  /** Slot → "HHMM", for naming the assignment windows the clear picker offers. */
  timeLabels: string[];
  operationMode: string | undefined;
  /** Collapses an officer's schedule into contiguous same-counter windows. */
  deriveWindows: (schedule: number[], timeLabels: string[]) => AssignmentWindow[];
  /** Empties the given slot ranges in the grid straight away, returning a rollback.
   *  The clearing is part of the assign request, but the user should not have to wait
   *  on the round trip to see the counters they just freed go empty. */
  onOptimisticClear?: (
    ranges: Array<{ officerKey: string; startSlot: number; endSlot: number }>,
  ) => () => void;
  /** The user's per-slot erase/break choices from the officer grid, so the modal shows
   *  exactly the windows the grid paints green and the backend fills the same slots. */
  breakOverrides?: BreakOverrides;
  /** Free-text duties (RosterState.duty_slots): working time, never offered to fill. */
  dutySlots?: Record<string, Record<string, string>>;
  onClose: () => void;
  /** Called after a successful batch assign so the parent can refresh the grid. */
  onDone: (result: OperationResult) => void;
}

function slotToHHMM(slot: number, startHour: number): string {
  const total = startHour * 60 + slot * 15;
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}`;
}

function fmt(hhmm: string): string {
  return `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`;
}

function durationLabel(slots: number): string {
  const mins = slots * 15;
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ''}`;
}

/**
 * Default break-size triple [1st, 2nd, 3rd] for a vacant window, computed
 * synchronously so the modal shows suggestions instantly (no API round-trip).
 * Mirrors the backend break_rules thresholds (backend/sos_scheduler.py +
 * backend/ot_scheduler.py) — the same source of truth the instant "Add SOS/OT"
 * UI uses — so the pre-filled defaults match what auto-assign would apply.
 */
function defaultBreakTriple(slots: number, officerType: string, isNight: boolean): (number | null)[] {
  let pattern: number[];
  if (officerType.toUpperCase() === 'OT') {
    // OT earns the same allocation as an SOS officer working (slots + 4).
    const eff = slots + 4;
    if (eff >= 36) pattern = isNight ? [10, 2, 2] : [2, 3, 3];
    else if (eff >= 28) pattern = [2, 3];
    else if (eff >= 11) pattern = [3];
    else pattern = [];
  } else {
    if (slots >= 36) pattern = isNight ? [10, 2, 2] : [2, 3, 3];
    else if (slots >= 28) pattern = [2, 3];
    else if (slots >= 20) pattern = [3];
    else if (slots >= 11) pattern = [2];
    else pattern = [];
  }
  return [pattern[0] ?? null, pattern[1] ?? null, pattern[2] ?? null];
}

/**
 * Modal for jointly auto-assigning the unassigned slots of several SOS/OT officers.
 *
 * Mirrors the initial Add SOS UI: every officer that currently has vacant (green) time
 * is listed, pre-selected, with each of their vacant availability windows shown as its
 * own row carrying three break-size dropdowns (1st/2nd/3rd break) — so breaks can be
 * tuned per window. Running the assign calls a single backend pass that balances counters
 * across the whole selected group (a shared optimum) instead of the per-officer local
 * optimum from the popup's per-officer auto-assign.
 */
export function SharedAutoAssignModal({
  officer_schedules, officer_metadata, shiftType, timeLabels, operationMode,
  deriveWindows, onOptimisticClear, breakOverrides, dutySlots, onClose, onDone,
}: Props) {
  const { notifySuccess, notifyError } = useNotification();
  const [submitting, setSubmitting] = useState(false);
  const startHour = getStartHour(shiftType);
  const isNight = shiftType === 'night';

  // Two-step flow: page 1 picks WHO/WHAT (free up time, then select officers/windows
  // to fill); page 2 is only shown when there's something to fill, and lets the user
  // tune break sizes for just the selected windows. Nothing reaches the backend until
  // the final confirm on page 2 (or the page-1 button when there's nothing to break-tune) —
  // "Back" from page 2 returns to page 1 with every choice intact, and the whole modal
  // stays purely local state until then, so a full close/cancel at any point reverts
  // everything with no backend call ever having been made.
  const [page, setPage] = useState<'select' | 'breaks'>('select');
  // Hierarchy is collapsed by default at both levels: officer-type groups, then each
  // officer's own vacant windows — mirroring the officer graph's own grouping/order
  // (naturalSortOfficers, below) so this list is not a second, independently-ordered
  // source of truth for "what order do officers come in".
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [expandedOfficers, setExpandedOfficers] = useState<Set<string>>(new Set());
  const toggleTypeExpanded = (type: string) => setExpandedTypes(prev => {
    const next = new Set(prev);
    if (next.has(type)) next.delete(type); else next.add(type);
    return next;
  });
  const toggleOfficerExpanded = (key: string) => setExpandedOfficers(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Existing counter assignments the user wants freed up as part of THIS batch. Keyed
  // "officerKey#windowIndex" over each officer's contiguous same-counter windows.
  const [clearWindows, setClearWindows] = useState<Set<string>>(() => new Set());
  const [clearOpen, setClearOpen] = useState(false);

  // The schedules the fill is planned against: today's, minus whatever the user has
  // ticked to clear. Deriving the candidate list from this is what makes the two halves
  // one operation rather than two — tick a window to free up and it appears in the fill
  // list immediately, with the break dropdowns it will actually be planned with.
  const effectiveSchedules = useMemo(() => {
    if (clearWindows.size === 0) return officer_schedules;
    const next: Record<string, number[]> = { ...officer_schedules };
    for (const id of clearWindows) {
      const hash = id.lastIndexOf('#');
      const key = id.slice(0, hash);
      const idx = parseInt(id.slice(hash + 1), 10);
      const win = deriveWindows(officer_schedules[key] ?? [], timeLabels)[idx];
      if (!win) continue;
      const sched = [...(next[key] ?? officer_schedules[key] ?? [])];
      for (let t = win.startSlot; t < win.endSlot; t++) sched[t] = 0;
      next[key] = sched;
    }
    return next;
  }, [officer_schedules, clearWindows, deriveWindows, timeLabels]);

  // The ranges those ticks stand for — as times for the request, and as slots for the
  // instant grid update that goes with it.
  const { clearRanges, clearSlotRanges } = useMemo(() => {
    const times: Array<{ officer_key: string; start_time: string; end_time: string }> = [];
    const slots: Array<{ officerKey: string; startSlot: number; endSlot: number }> = [];
    for (const id of clearWindows) {
      const hash = id.lastIndexOf('#');
      const key = id.slice(0, hash);
      const idx = parseInt(id.slice(hash + 1), 10);
      const win = deriveWindows(officer_schedules[key] ?? [], timeLabels)[idx];
      if (!win) continue;
      times.push({ officer_key: key, start_time: win.start, end_time: win.end });
      slots.push({ officerKey: key, startSlot: win.startSlot, endSlot: win.endSlot });
    }
    return { clearRanges: times, clearSlotRanges: slots };
  }, [clearWindows, officer_schedules, deriveWindows, timeLabels]);

  // The grid's erase/break overrides, plus the time ticked to clear here: freed time is time
  // to re-plan, so it counts as erased (green) however short — a counter cleared from the
  // middle of an officer's day would otherwise read as a break and never be filled back.
  // The backend folds clear_ranges in the same way.
  const overrides = useMemo((): BreakOverrides => {
    if (clearSlotRanges.length === 0) return breakOverrides ?? {};
    const cleared: Record<string, number[]> = { ...(breakOverrides?.cleared ?? {}) };
    for (const r of clearSlotRanges) {
      const slots = [...(cleared[r.officerKey] ?? [])];
      for (let t = r.startSlot; t < r.endSlot; t++) slots.push(t);
      cleared[r.officerKey] = slots;
    }
    return { ...breakOverrides, cleared };
  }, [breakOverrides, clearSlotRanges]);

  // Candidate officers: SOS/OT with at least one green slot (folding in the user's per-slot
  // erase/break overrides so the list matches what the officer grid paints green).
  const candidates = useMemo(
    () => officersWithUnassignedTime(effectiveSchedules, officer_metadata, shiftType, overrides, dutySlots),
    [effectiveSchedules, officer_metadata, shiftType, overrides, dutySlots],
  );

  // Grouped by officer type, in the SAME order the officer graph lists officers
  // in — one shared source of truth (groupOfficersByType) instead of a second,
  // independently-sorted list here.
  const officerOrder = useOfficerOrder();   // re-sort when the user drags rows
  const groupedCandidates = useMemo(() => (
    groupOfficersByType(candidates, c => c.key, c => c.type as OfficerTypeGroup)
      .map(({ type, items }) => ({ type, officers: items }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [candidates, officerOrder]);

  // A window's identity for the selection and break maps is its officer and START SLOT,
  // not its position in the list: freeing up time renumbers an officer's vacant windows,
  // and a choice pinned to an index would silently jump to a different window.
  const winId = (key: string, startSlot: number) => `${key}@${startSlot}`;

  // Windows the user has switched OFF. Everything else is on, so a window that appears
  // because the user just freed up its time arrives selected, the way the rest of the
  // list did when the modal opened.
  const [deselected, setDeselected] = useState<Set<string>>(() => new Set());
  const isOn = useCallback(
    (key: string, startSlot: number) => !deselected.has(winId(key, startSlot)),
    [deselected],
  );
  const selectedCount = useMemo(
    () => candidates.reduce(
      (n, c) => n + c.vacantBlocks.filter(([s]) => isOn(c.key, s)).length, 0),
    [candidates, isOn],
  );
  const totalWindows = useMemo(
    () => candidates.reduce((n, c) => n + c.vacantBlocks.length, 0), [candidates]);

  // Editable name / deployment per officer, seeded from metadata — mirrors the Add SOS row so
  // the user can correct an officer's details right here before assigning. Persisted on run.
  const [edits, setEdits] = useState<Record<string, { name: string; deployment: string }>>({});
  const editOf = (key: string) => edits[key] ?? {
    name: officer_metadata[key]?.name ?? '',
    deployment: officer_metadata[key]?.deployment ?? '',
  };
  const setEdit = (key: string, field: 'name' | 'deployment', value: string) =>
    setEdits(prev => ({ ...prev, [key]: { ...editOf(key), [field]: value } }));

  // Per-window break sizes, held sparsely: a window the user has not touched answers with
  // its default pattern (mirroring the instant Add SOS/OT UI) rather than being seeded into
  // state up front, which would go stale the moment freeing up time changes the list.
  const [breaks, setBreaks] = useState<Record<string, (number | null)[]>>({});
  const breakTriple = (c: { key: string; type: string }, block: [number, number]): (number | null)[] =>
    breaks[winId(c.key, block[0])] ?? defaultBreakTriple(block[1] - block[0], c.type, isNight);

  const toggleWindow = (key: string, startSlot: number) => setDeselected(prev => {
    const next = new Set(prev);
    const id = winId(key, startSlot);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const officerSelState = (c: { key: string; vacantBlocks: Array<[number, number]> }): 'all' | 'some' | 'none' => {
    const n = c.vacantBlocks.filter(([s]) => isOn(c.key, s)).length;
    return n === 0 ? 'none' : n === c.vacantBlocks.length ? 'all' : 'some';
  };
  const toggleOfficer = (c: { key: string; vacantBlocks: Array<[number, number]> }) => setDeselected(prev => {
    const next = new Set(prev);
    const ids = c.vacantBlocks.map(([s]) => winId(c.key, s));
    if (ids.every(id => !next.has(id))) ids.forEach(id => next.add(id));
    else ids.forEach(id => next.delete(id));
    return next;
  });

  const setBreak = (key: string, startSlot: number, breakIdx: number, val: number | null,
                    current: (number | null)[]) =>
    setBreaks(prev => ({
      ...prev,
      [winId(key, startSlot)]: current.map((s, i) => (i === breakIdx ? val : s)),
    }));

  const allSelected = totalWindows > 0 && selectedCount === totalWindows;
  const toggleAll = () => setDeselected(
    allSelected
      ? new Set(candidates.flatMap(c => c.vacantBlocks.map(([s]) => winId(c.key, s))))
      : new Set(),
  );

  const run = async () => {
    if (selectedCount === 0 && clearRanges.length === 0) return;
    setSubmitting(true);
    // Empty the freed counters on screen now; the request that follows does the same
    // thing for real, and this is undone if it fails.
    const rollbackClear = clearSlotRanges.length > 0 && onOptimisticClear
      ? onOptimisticClear(clearSlotRanges)
      : null;
    try {
      // Officers with at least one selected window, and — per officer — the exact window
      // indices to fill. When every window of an officer is selected, omit them from
      // window_selection so the backend fills all (its default).
      const officerKeys: string[] = [];
      const windowSelection: Record<string, number[]> = {};
      for (const c of candidates) {
        const idxs = c.vacantBlocks.map((_, i) => i).filter(i => isOn(c.key, c.vacantBlocks[i][0]));
        if (idxs.length === 0) continue;
        officerKeys.push(c.key);
        if (idxs.length < c.vacantBlocks.length) windowSelection[c.key] = idxs;
      }
      // Per selected officer, send a per-window list of break sizes (aligned with the
      // officer's vacant-block order — the backend applies them only to the windows it
      // fills). Windows left on defaults send an empty list.
      const map: Record<string, number[][]> = {};
      for (const c of candidates) {
        if (!officerKeys.includes(c.key)) continue;
        const perBlock = c.vacantBlocks.map(
          b => breakTriple(c, b).filter((s): s is number => s !== null));
        if (perBlock.some(b => b.length > 0)) map[c.key] = perBlock;
      }
      // Name/deployment edits ride along in the SAME batch call below (metadata_changes)
      // rather than a separate metadata request — one edit, one history entry.
      const metaChanges: Record<string, { name: string; deployment: string }> = {};
      for (const key of officerKeys) {
        const e = edits[key];
        const orig = officer_metadata[key];
        if (!e) continue;
        if (e.name !== (orig?.name ?? '') || e.deployment !== (orig?.deployment ?? '')) {
          metaChanges[key] = { name: e.name, deployment: e.deployment };
        }
      }

      // Forward the user's per-slot overrides (only for the officers being filled) so the
      // backend's green computation matches the modal's exactly.
      const cleared: Record<string, number[]> = {};
      const userBreaks: Record<string, number[]> = {};
      for (const key of officerKeys) {
        if (overrides.cleared?.[key]?.length) cleared[key] = overrides.cleared[key];
        if (overrides.userBreaks?.[key]?.length) userBreaks[key] = overrides.userBreaks[key];
      }
      // One call, so freeing up time and filling it land as a single undoable version
      // rather than one per officer cleared plus one for the assign.
      const result = await apiClient.smartFillBatch({
        officer_keys: officerKeys,
        metadata_changes: Object.keys(metaChanges).length > 0 ? metaChanges : undefined,
        custom_break_sizes_map: Object.keys(map).length > 0 ? map : undefined,
        window_selection: Object.keys(windowSelection).length > 0 ? windowSelection : undefined,
        cleared_break_slots: Object.keys(cleared).length > 0 ? cleared : undefined,
        user_break_slots: Object.keys(userBreaks).length > 0 ? userBreaks : undefined,
        clear_ranges: clearRanges.length > 0 ? clearRanges : undefined,
      });
      notifySuccess(result.message);
      onDone(result);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      rollbackClear?.();
      notifyError(`Shared auto-assign failed: ${msg}`);
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 4600,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 12, width: 'min(520px, 100%)', zoom: UI_SCALE, maxHeight: popupMaxH(82),
          display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ padding: '16px 18px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
            Auto-assign (batch)
            {page === 'breaks' && <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}> — step 2: break durations</span>}
          </div>
          {/* Same cap as the Add Officers header — this button plans breaks too. Only
              relevant while still picking who/what, so it's hidden on the breaks page. */}
          {page === 'select' && <>
            <MaxCounterDurationField compact />
            <HandoffOverlapToggle compact />
          </>}
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {page === 'select' ? (
            <>
              {/* Free up counter time — the other half of the same batch. Ticking a window
                  here empties it, which puts it straight into the fill list below, and the
                  whole thing goes to the backend as one edit (nothing is sent until the
                  final confirm on step 2). */}
              <div style={{ margin: '0 12px 8px', border: '1px solid #fecaca', borderRadius: 8, background: '#fef2f2' }}>
                <button
                  onClick={() => setClearOpen(o => !o)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                    background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>
                    {clearOpen ? '▾' : '▸'} Delete existing ctrs
                  </span>
                  <span style={{ fontSize: 11, color: '#b91c1c', marginLeft: 'auto', fontWeight: 600 }}>
                    {clearRanges.length > 0
                      ? `${clearRanges.length} to clear`
                      : 'optional'}
                  </span>
                </button>
                {clearOpen && (
                  <div style={{ padding: '0 10px 10px' }}>
                    <div style={{ fontSize: 11, color: '#7f1d1d', marginBottom: 6 }}>
                      Select officers you wish to re-assign counters
                    </div>
                    <ClearAssignmentsPicker
                      officerSchedules={officer_schedules}
                      officerMetadata={officer_metadata}
                      timeLabels={timeLabels}
                      operationMode={operationMode}
                      selected={clearWindows}
                      onChange={setClearWindows}
                      deriveWindows={deriveWindows}
                    />
                  </div>
                )}
              </div>

              {candidates.length === 0 ? (
                <div style={{ padding: '4px 18px 18px', fontSize: 13, color: '#64748b' }}>
                  No officer has unassigned time right now. Open “Delete existing ctrs”
                  above and tick the assignments you want re-planned — they will be cleared and
                  filled back in together.
                </div>
              ) : (
                <>
                <div style={{ padding: '2px 18px 6px', fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>
                  These are vacant windows — time with no counter assigned yet — ready to be filled by auto-assign. (An officer already on a counter must be deleted via “Delete existing ctrs” above before that time can be re-planned here.)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 18px 8px' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {selectedCount} of {totalWindows} window{totalWindows === 1 ? '' : 's'} to fill
                  </span>
                  <button
                    onClick={toggleAll}
                    style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '2px 4px' }}
                  >
                    {allSelected ? 'Clear all' : 'Select all'}
                  </button>
                </div>
                <div style={{ padding: '0 12px 4px' }}>
                  {/* Hierarchy: officer-type group → officer → window, each collapsed by
                      default, in the officer graph's own order (naturalSortOfficers) so this
                      is not a second, independently-ordered listing of the same officers. */}
                  {groupedCandidates.map(group => {
                    const groupSelected = group.officers.reduce(
                      (n, c) => n + c.vacantBlocks.filter(([s]) => isOn(c.key, s)).length, 0);
                    const groupTotal = group.officers.reduce((n, c) => n + c.vacantBlocks.length, 0);
                    const groupOpen = expandedTypes.has(group.type);
                    const isSOS = group.type === 'SOS';
                    const pill = OFFICER_TYPE_PILL[group.type];
                    const pillStyle = {
                      fontSize: 12, fontWeight: 800, padding: '2px 8px', borderRadius: 99, flexShrink: 0,
                      background: pill.bg, color: pill.fg,
                    };
                    return (
                      <div key={group.type} style={{ marginBottom: 6, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                        <button
                          onClick={() => toggleTypeExpanded(group.type)}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px',
                            background: groupOpen ? '#f8fafc' : 'white', border: 'none', cursor: 'pointer', textAlign: 'left',
                          }}
                        >
                          <span style={{ color: '#94a3b8', fontSize: 11, flexShrink: 0 }}>{groupOpen ? '▾' : '▸'}</span>
                          <span style={pillStyle}>{group.type}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
                            {group.officers.length} officer{group.officers.length === 1 ? '' : 's'}
                          </span>
                          <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto', fontWeight: 600 }}>
                            {groupSelected}/{groupTotal} window{groupTotal === 1 ? '' : 's'}
                          </span>
                        </button>

                        {groupOpen && (
                          <div style={{ padding: '4px 8px 8px' }}>
                            {group.officers.map(c => {
                              const selState = officerSelState(c);
                              const on = selState !== 'none';
                              const officerOpen = expandedOfficers.has(c.key);
                              const officerSelected = c.vacantBlocks.filter(([s]) => isOn(c.key, s)).length;
                              const ed = editOf(c.key);
                              return (
                                <div
                                  key={c.key}
                                  style={{
                                    padding: '6px 6px', borderRadius: 8, marginBottom: 4,
                                    background: on ? '#eef2ff' : 'transparent',
                                    opacity: on ? 1 : 0.6,
                                  }}
                                >
                                  {/* Officer row — id shown with its type pill (same as the officer
                                      card), then editable name + deployment. The checkbox toggles all
                                      of this officer's windows (indeterminate when only some are on). */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <input
                                      type="checkbox"
                                      checked={selState === 'all'}
                                      ref={el => { if (el) el.indeterminate = selState === 'some'; }}
                                      onChange={() => toggleOfficer(c)}
                                      style={{ width: 16, height: 16, accentColor: '#6366f1', flexShrink: 0 }}
                                    />
                                    <span style={pillStyle}>{c.type}</span>
                                    <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: '#333', flexShrink: 0 }}>
                                      {c.key}
                                    </span>
                                    <input
                                      type="text" value={ed.name} placeholder="Name"
                                      onChange={e => setEdit(c.key, 'name', e.target.value)}
                                      style={{ padding: '3px 6px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, fontWeight: 600, background: '#fafafa', minWidth: 0, flex: 1, boxSizing: 'border-box' }}
                                    />
                                    {isSOS && (
                                      <input
                                        type="text" value={ed.deployment} placeholder="Deployment"
                                        onChange={e => setEdit(c.key, 'deployment', e.target.value)}
                                        style={{ padding: '3px 5px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, fontWeight: 600, background: '#fafafa', minWidth: 0, flex: 1, boxSizing: 'border-box' }}
                                      />
                                    )}
                                    <button
                                      onClick={() => toggleOfficerExpanded(c.key)}
                                      title={officerOpen ? 'Hide windows' : 'Show windows'}
                                      style={{
                                        background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8',
                                        fontSize: 11, fontWeight: 600, flexShrink: 0, padding: '2px 4px', display: 'flex', alignItems: 'center', gap: 3,
                                      }}
                                    >
                                      {officerSelected}/{c.vacantBlocks.length} {officerOpen ? '▾' : '▸'}
                                    </button>
                                  </div>

                                  {/* Vacant windows — collapsed by default. Just pick WHO/WHAT here;
                                      break sizes are configured on step 2, only for what's selected. */}
                                  {officerOpen && c.vacantBlocks.map(([s, e], blockIdx) => {
                                    const durSlots = e - s;
                                    const winOn = isOn(c.key, s);
                                    return (
                                      <div
                                        key={blockIdx}
                                        onClick={() => toggleWindow(c.key, s)}
                                        style={{
                                          margin: '4px 0 0 36px', padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
                                          display: 'flex', alignItems: 'center', gap: 8,
                                          background: winOn ? 'rgba(255,255,255,0.7)' : 'transparent',
                                          border: '1px solid #e2e8f0',
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={winOn}
                                          onChange={() => toggleWindow(c.key, s)}
                                          onClick={e => e.stopPropagation()}
                                          style={{ width: 14, height: 14, accentColor: '#6366f1', flexShrink: 0 }}
                                        />
                                        {c.vacantBlocks.length > 1 && (
                                          <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8' }}>#{blockIdx + 1}</span>
                                        )}
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
                                          {fmt(slotToHHMM(s, startHour))}–{fmt(slotToHHMM(e, startHour))}
                                        </span>
                                        <span style={{ fontSize: 11, color: '#94a3b8' }}>{durationLabel(durSlots)}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                </>
              )}
            </>
          ) : (
            /* ── Step 2: break durations, only for what step 1 left selected ── */
            <div style={{ padding: '4px 12px 8px' }}>
              <div style={{ fontSize: 12, color: '#64748b', padding: '2px 6px 10px' }}>
                Tune break sizes for the {selectedCount} window{selectedCount === 1 ? '' : 's'} you selected.
                Leaving a break as "None" uses the automatic default.
              </div>
              {groupedCandidates.map(group => {
                const officersWithSelection = group.officers.filter(
                  c => c.vacantBlocks.some(([s]) => isOn(c.key, s)));
                if (officersWithSelection.length === 0) return null;
                const pill = OFFICER_TYPE_PILL[group.type];
                const pillStyle = {
                  fontSize: 12, fontWeight: 800, padding: '2px 8px', borderRadius: 99, flexShrink: 0,
                  background: pill.bg, color: pill.fg,
                };
                return (
                  <div key={group.type} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px 6px' }}>
                      <span style={pillStyle}>{group.type}</span>
                    </div>
                    {officersWithSelection.map(c => {
                      const ed = editOf(c.key);
                      return (
                        <div key={c.key} style={{ marginBottom: 6, padding: '4px 4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                            <span style={pillStyle}>{c.type}</span>
                            <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#333' }}>{c.key}</span>
                            <span style={{ fontSize: 12, color: '#64748b' }}>{ed.name}</span>
                          </div>
                          {c.vacantBlocks.filter(([s]) => isOn(c.key, s)).map(([s, e]) => {
                            const sizes = breakTriple(c, [s, e]);
                            return (
                              <div
                                key={s}
                                style={{ margin: '0 0 4px 20px', padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: 'rgba(255,255,255,0.7)' }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
                                    {fmt(slotToHHMM(s, startHour))}–{fmt(slotToHHMM(e, startHour))}
                                  </span>
                                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{durationLabel(e - s)}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
                                  {(['1st break', '2nd break', '3rd break'] as const).map((label, bIdx) => {
                                    const slotsVal = sizes[bIdx];
                                    const palette = slotsVal != null ? getDurationPalette(slotsVal * 15, 15) : null;
                                    return (
                                      <div key={bIdx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                        <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{label}</span>
                                        <select
                                          value={slotsVal ?? ''}
                                          onChange={ev => setBreak(c.key, s, bIdx, ev.target.value === '' ? null : parseInt(ev.target.value), sizes)}
                                          style={{
                                            width: '100%', padding: '2px 1px',
                                            border: palette ? 'none' : '1px solid #e2e8f0',
                                            borderRadius: 4, fontSize: 10,
                                            background: palette?.bg ?? 'white',
                                            color: palette?.textColor ?? '#94a3b8',
                                            fontWeight: palette ? 600 : 400,
                                            cursor: 'pointer',
                                          }}
                                        >
                                          <option value="">None</option>
                                          {BREAK_OPTIONS.map(opt => (
                                            <option key={opt.slots} value={opt.slots}>{opt.label}</option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid #f1f5f9' }}>
          {page === 'select' ? (
            <>
              <button
                onClick={onClose}
                style={{ padding: '8px 14px', border: '1px solid #e2e8f0', background: 'white', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#475569' }}
              >
                Cancel
              </button>
              <button
                onClick={() => (selectedCount > 0 ? setPage('breaks') : run())}
                disabled={submitting || (selectedCount === 0 && clearRanges.length === 0)}
                style={{
                  padding: '8px 16px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: submitting || (selectedCount === 0 && clearRanges.length === 0) ? '#c7d2fe' : '#6366f1',
                  color: 'white',
                  cursor: submitting || (selectedCount === 0 && clearRanges.length === 0) ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting
                  ? 'Working…'
                  : selectedCount > 0
                    ? `Next: break durations (${selectedCount}) →`
                    : clearRanges.length > 0
                      ? `Clear ${clearRanges.length}`
                      : 'Auto-assign'}
              </button>
            </>
          ) : (
            <>
              {/* Reverts to step 1 with every choice intact — nothing has reached the
                  backend yet, so "cancel out of step 2" is just this. */}
              <button
                onClick={() => setPage('select')}
                style={{ padding: '8px 14px', border: '1px solid #e2e8f0', background: 'white', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#475569', marginRight: 'auto' }}
              >
                ← Back
              </button>
              <button
                onClick={onClose}
                style={{ padding: '8px 14px', border: '1px solid #e2e8f0', background: 'white', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#475569' }}
              >
                Cancel
              </button>
              <button
                onClick={run}
                disabled={submitting}
                style={{
                  padding: '8px 16px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: submitting ? '#c7d2fe' : '#6366f1',
                  color: 'white',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting
                  ? 'Working…'
                  : clearRanges.length > 0
                    ? `Clear ${clearRanges.length} & auto-assign ${selectedCount || ''}`.trim()
                    : `Auto-assign ${selectedCount || ''}`.trim()}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
